import { MAP_SIZE, MAP_ROUTES, AREA_BY_ID, type AreaId, type Zone } from "../../content/map";

// 和色の路: 外縁（漆色）→ 路面（和紙色系）
const ZONE_ROAD: Record<Zone, { outer: string; inner: string }> = {
  prologue: { outer: "#8A2018", inner: "#F0C8A0" }, // 朱漆 + 桃色
  arrival:  { outer: "#183050", inner: "#A8C0D0" }, // 深紺 + 浅葱
  life:     { outer: "#1E4828", inner: "#A8C890" }, // 深緑 + 若草
};
const STONE_RADIUS = 10;
const STONE_RING_RADIUS = 16;
const STONE_HIT_RADIUS = 18;
const NODE_CLEAR_RADIUS = 92;

/**
 * 和風路線レイヤー。
 * 飛び石10個以下は二次曲線、15個以上は三角関数の波線でゲームボードの主導線にする。
 */
export function RouteLayer({
  progressByArea,
  isUnlocked,
  onCourseTap,
}: {
  progressByArea: Partial<Record<AreaId, { done: number; total: number; cleared: boolean }>>;
  isUnlocked: (id: AreaId) => boolean;
  onCourseTap: (areaId: AreaId, courseIndex: number) => void;
}) {
  return (
    <svg
      className="absolute top-0 left-0"
      width={MAP_SIZE.width}
      height={MAP_SIZE.height}
    >
      <defs>
        <filter id="route-shadow" x="-25%" y="-80%" width="150%" height="260%">
          <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#2A1808" floodOpacity="0.28" />
        </filter>
      </defs>

      {MAP_ROUTES.map(({ from, to }, idx) => {
        const a = AREA_BY_ID[from].position;
        const b = AREA_BY_ID[to].position;
        const { outer, inner } = ZONE_ROAD[AREA_BY_ID[from].zone];
        const progress = progressByArea[from];
        const stoneTotal = Math.max(1, progress?.total ?? 5);
        const routeOpen = isUnlocked(from);
        const done = progress?.cleared
          ? stoneTotal
          : Math.min(stoneTotal, progress?.done ?? 0);

        const route = buildRouteSamples(a, b, idx, stoneTotal);
        const pathD = samplesToPath(route.samples);

        const arrow = pointAtDistance(route.samples, route.length * 0.52);
        const nextArrow = pointAtDistance(route.samples, Math.min(route.length, route.length * 0.52 + 10));
        const tangent = { x: nextArrow.x - arrow.x, y: nextArrow.y - arrow.y };
        const dtx = tangent.x;
        const dty = tangent.y;
        const arrowAngle = Math.atan2(dty, dtx) * (180 / Math.PI);
        const steppingStones = steppingStonePoints(route.samples, stoneTotal);

        return (
          <g key={`${from}-${to}`}>
            {/* 街道本体: 漆縁 → 路面 の2層。太めにしてゲームボードの主導線にする。 */}
            <g filter="url(#route-shadow)" style={{ pointerEvents: "none" }}>
              <path d={pathD} stroke={outer} strokeWidth={34} fill="none" strokeLinecap="round" />
              <path d={pathD} stroke={inner} strokeWidth={18} fill="none" strokeLinecap="round" />
            </g>

            {/* 踏み石。from エリア内の各コース完了で左から順に埋まっていく。 */}
            {steppingStones.map((p, stoneIdx) => (
              <g
                key={stoneIdx}
                transform={`translate(${p.x},${p.y})`}
                className={routeOpen && stoneIdx <= done ? "pointer-events-auto cursor-pointer" : "pointer-events-none"}
                onPointerDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  if (routeOpen && stoneIdx <= done) onCourseTap(from, stoneIdx);
                }}
              >
                <circle r={STONE_HIT_RADIUS} fill="transparent" />
                <circle
                  r={STONE_RADIUS}
                  fill={stoneIdx < done ? outer : stoneIdx === done && routeOpen ? "#FFF8D8" : "#F7EBC8"}
                  stroke={outer}
                  strokeWidth={stoneIdx === done && routeOpen ? 3 : 2.2}
                  opacity={routeOpen ? stoneIdx <= done ? 0.98 : 0.7 : 0.34}
                />
                {stoneIdx < done && (
                  <path
                    d="M-3 0.5 0 3.5 4 -4"
                    fill="none"
                    stroke="#F7EBC8"
                    strokeWidth={2.6}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
                {stoneIdx === done && routeOpen && (
                  <circle
                    r={STONE_RING_RADIUS}
                    fill="none"
                    stroke="#C8940A"
                    strokeWidth={2}
                    opacity={0.85}
                  />
                )}
              </g>
            ))}

            {/* 進行方向: 矢印サークル（小さめ・品のある形） */}
            <g transform={`translate(${arrow.x},${arrow.y}) rotate(${arrowAngle})`} style={{ pointerEvents: "none" }}>
              <circle r={17} fill={outer} opacity={0.92} />
              <polygon points="-6,-6 9,0 -6,6" fill="#F5EFE0" />
            </g>
          </g>
        );
      })}
    </svg>
  );
}

function buildRouteSamples(
  a: { x: number; y: number },
  b: { x: number; y: number },
  idx: number,
  stoneTotal: number,
) {
  const samples = stoneTotal >= 15
    ? sampleSineRoute(a, b, idx, stoneTotal, 144)
    : sampleQuadraticRoute(a, b, idx, stoneTotal, 96);
  return {
    samples,
    length: samples[samples.length - 1].distance,
  };
}

function sampleQuadraticRoute(
  a: { x: number; y: number },
  b: { x: number; y: number },
  idx: number,
  _stoneTotal: number,
  steps: number,
) {
  const segAngle = Math.atan2(b.y - a.y, b.x - a.x);
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const perp = Math.min(Math.max(len * 0.14, 44), 96);
  const side = idx % 2 === 0 ? 1 : -1;
  const c = {
    x: (a.x + b.x) / 2 + Math.sin(segAngle) * perp * side,
    y: (a.y + b.y) / 2 - Math.cos(segAngle) * perp * side,
  };
  return sampleParametric(steps, (t) => pointOnQuadratic(a, c, b, t));
}

function sampleSineRoute(
  a: { x: number; y: number },
  b: { x: number; y: number },
  idx: number,
  _stoneTotal: number,
  steps: number,
) {
  const segAngle = Math.atan2(b.y - a.y, b.x - a.x);
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  const density = Math.min(1, Math.max(0, (_stoneTotal - 10) / 10));
  const waves = 1 + density * 0.5;
  const side = idx % 2 === 0 ? 1 : -1;
  const ux = Math.cos(segAngle);
  const uy = Math.sin(segAngle);
  const nx = Math.sin(segAngle) * side;
  const ny = -Math.cos(segAngle) * side;
  const amplitude = Math.min(Math.max(len * (0.10 + density * 0.08), 48), 120);
  return sampleParametric(steps, (t) => {
    const taper = Math.sin(Math.PI * t);
    const wave = Math.sin(Math.PI * 2 * waves * t);
    return {
      x: a.x + ux * len * t + nx * amplitude * taper * wave,
      y: a.y + uy * len * t + ny * amplitude * taper * wave,
    };
  });
}

function steppingStonePoints(
  samples: Array<{ x: number; y: number; distance: number }>,
  stoneTotal: number,
) {
  const pathLength = samples[samples.length - 1].distance;
  if (stoneTotal <= 1) return [pointAtDistance(samples, pathLength / 2)];

  const padding = Math.min(
    NODE_CLEAR_RADIUS,
    Math.max(48, pathLength * 0.22),
  );
  const startDistance = padding;
  const endDistance = Math.max(startDistance, pathLength - padding);
  const usableDistance = endDistance - startDistance;

  return Array.from({ length: stoneTotal }, (_, stoneIdx) =>
    pointAtDistance(
      samples,
      startDistance + usableDistance * (stoneIdx / (stoneTotal - 1)),
    ),
  );
}

function sampleParametric(
  steps: number,
  pointAt: (t: number) => { x: number; y: number },
) {
  let distance = 0;
  let prev = pointAt(0);
  const samples = [{ ...prev, distance }];
  for (let i = 1; i <= steps; i += 1) {
    const p = pointAt(i / steps);
    distance += Math.hypot(p.x - prev.x, p.y - prev.y);
    samples.push({ ...p, distance });
    prev = p;
  }
  return samples;
}

function samplesToPath(samples: Array<{ x: number; y: number }>) {
  return samples
    .map((p, idx) => `${idx === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
    .join(" ");
}

function pointAtDistance(
  samples: Array<{ x: number; y: number; distance: number }>,
  target: number,
) {
  const end = samples[samples.length - 1];
  if (target <= 0) return samples[0];
  if (target >= end.distance) return end;

  for (let i = 1; i < samples.length; i += 1) {
    const curr = samples[i];
    if (curr.distance < target) continue;
    const prev = samples[i - 1];
    const segment = Math.max(1, curr.distance - prev.distance);
    const ratio = (target - prev.distance) / segment;
    return {
      x: prev.x + (curr.x - prev.x) * ratio,
      y: prev.y + (curr.y - prev.y) * ratio,
    };
  }
  return end;
}

function pointOnQuadratic(
  a: { x: number; y: number },
  c: { x: number; y: number },
  b: { x: number; y: number },
  t: number,
) {
  const u = 1 - t;
  return {
    x: u * u * a.x + 2 * u * t * c.x + t * t * b.x,
    y: u * u * a.y + 2 * u * t * c.y + t * t * b.y,
  };
}
